import React, { useContext, useEffect, useState } from "react";
import { BoxWrapper } from "../../../style";
import arrowIcon from "../../../images/arrow.png";
import { FormWrapper2 } from "../../../components/loan/style";
import Button from "../../../components/ui/Button";
import Alert from "../../../components/ui/Alert";
import { useNavigate } from "react-router-dom";
import { getStorage, goBack, setStorage } from "../../../Utils/common";
import PictureUpload from "../../../components/PictureUpload/PictureUpload";
import { saveDocuments, requiredDocs, getDashboardData } from "../../../Utils/api";
import ContextDashboard from "../../../Context/ContextDashboard";



function DocumentUpload() {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [documentsRequired, setDocumentsRequired] = useState([]);
    const [uploadedDocuments, setUploadedDocuments] = useState({});
    const [documentPasswords, setDocumentPasswords] = useState({}); // Store passwords for each document
    const [isLoadingDocs, setIsLoadingDocs] = useState(true); // Loading state for docs
    const [isApplicationSubmitted, setIsApplicationSubmitted] = useState(false);
    const navigate = useNavigate();
    const { logout, handleEvent } = useContext(ContextDashboard);

    // Fetch required documents when the component mounts
    useEffect(() => {
        // First check if application is already submitted from storage
        const storedApplicationStatus = getStorage("application_status");
        if (storedApplicationStatus === "submitted") {
            console.log("Application already submitted from storage - setting frozen state");
            setIsApplicationSubmitted(true);
        }

        const params = { profile_id: getStorage("cust_profile_id") || "" };
        setIsLoadingDocs(true); // Set loading state to true before API call
        
        // First refresh the page data to get latest information
        const refreshPageData = async () => {
            try {
                // Get latest dashboard data to refresh profile information
                const dashboardResponse = await getDashboardData(params);
                if (dashboardResponse?.data?.Status === 1) {
                    console.log("Page data refreshed successfully");
                    // Update any relevant data from dashboard if needed
                }
            } catch (error) {
                console.log("Error refreshing page data:", error);
            }
        };

        // Refresh page data first, then fetch required documents
        refreshPageData().then(() => {
            return requiredDocs(params);
        }).then(resp => {
            if (resp?.data?.Status === 1) {
                const docs = resp?.data?.Data || [];
                // Force PDF format for salary slip and bank statement documents
                const processedDocs = docs.map(doc => {
                    const docName = doc.name.toLowerCase();
                    if (docName.includes('salary') || docName.includes('bank') || docName.includes('statement')) {
                        return { ...doc, allowed_format: 'pdf' };
                    }
                    return doc;
                });
                
                setDocumentsRequired(processedDocs);
                setMessage({ type: 'success', msg: resp?.data?.Message, place: "globle" });

            } else {
                setMessage({ type: "error", msg: resp?.data?.Message });
            }
            setIsLoadingDocs(false); // Set loading state to false once data is fetched
        }).catch(err => {
            setMessage({ type: "error", msg: "Failed to fetch required documents." });
            setIsLoadingDocs(false); // Set loading state to false on error
        });
    }, []);

    // Handle page refresh - re-send documents to backend
    useEffect(() => {
        const handlePageRefresh = () => {
            // Check if there are uploaded documents in localStorage
            const storedDocuments = getStorage("uploaded_documents");
            if (storedDocuments) {
                console.log("Page refreshed - re-sending documents to backend");
                // Re-send documents to backend to ensure authenticity
                reSendDocumentsToBackend(JSON.parse(storedDocuments));
            }
        };

        // Listen for page refresh
        window.addEventListener('beforeunload', handlePageRefresh);
        
        // Also check on component mount if page was refreshed
        const isPageRefresh = performance.navigation.type === 1;
        if (isPageRefresh) {
            handlePageRefresh();
        }

        return () => {
            window.removeEventListener('beforeunload', handlePageRefresh);
        };
    }, []);

    // Re-send documents to backend function
    const reSendDocumentsToBackend = async (storedDocs) => {
        try {
            const uploadPromises = [];
            
            for (const [docName, docData] of Object.entries(storedDocs)) {
                if (docData.file && docData.docInfo) {
                    const param = {
                        profile_id: getStorage("cust_profile_id") || "",
                        file_ext: docData.fileExt,
                        file_password: docData.password || "N/A",
                        event_name: docData.docInfo.event_name,
                        doc_type: docData.docInfo.doc_type,
                        file: docData.file,
                    };
                    uploadPromises.push(saveDocuments(param));
                }
            }

            if (uploadPromises.length > 0) {
                const responses = await Promise.all(uploadPromises);
                console.log("Documents re-sent to backend successfully:", responses);
            }
        } catch (error) {
            console.log("Error re-sending documents:", error);
        }
    };

    // Handle file change (image or document)
    const handleFileChange = (docName, file) => {
        setUploadedDocuments(prev => ({ ...prev, [docName]: file }));
    };

    // Handle password change for documents
    const handlePasswordChange = (docName, password) => {
        setDocumentPasswords(prev => ({ ...prev, [docName]: password }));
    };

    // Convert file to base64 for upload
    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",").pop());
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Submit the documents
    const submitDocuments = async () => {
        // Validation: Check if all required documents have been uploaded
        const missingDocuments = documentsRequired.filter(doc => !uploadedDocuments[doc.name]);
        if (missingDocuments.length > 0) {
            setMessage({ type: "error", msg: "Please upload all required documents." });
            return; // Stop the submission if there are missing documents
        }

        setLoading(true);

        const uploadPromises = [];
        for (const doc of documentsRequired) {
            const docName = doc.name;
            const file = uploadedDocuments[docName];
            if (file) {
                try {
                    const base64File = await convertToBase64(file);
                    const ext = file.name.split('.').pop().toUpperCase();
                    const param = {
                        profile_id: getStorage("cust_profile_id") || "",
                        file_ext: ext,
                        file_password: documentPasswords[docName] || "N/A",
                        event_name: doc.event_name,
                        doc_type: doc.doc_type,
                        file: base64File,
                    };
                    uploadPromises.push(saveDocuments(param));
                } catch (err) {
                    setMessage({ type: "error", msg: `Failed to upload ${docName}. Please try again.` });
                    setLoading(false);
                    return;
                }
            }
        }

        try {
            const responses = await Promise.all(uploadPromises);
            const successResponses = responses.filter(resp => resp?.data?.Status === 1);
            if (successResponses.length === uploadPromises.length) {
                // Store documents in localStorage for re-sending on refresh
                const documentsToStore = {};
                for (const doc of documentsRequired) {
                    const docName = doc.name;
                    const file = uploadedDocuments[docName];
                    if (file) {
                        documentsToStore[docName] = {
                            file: uploadedDocuments[docName],
                            password: documentPasswords[docName] || "",
                            fileExt: file.name.split('.').pop().toUpperCase(),
                            docInfo: doc
                        };
                    }
                }
                setStorage("uploaded_documents", JSON.stringify(documentsToStore));
                
                // Show completion message with lead_id
                const leadId = successResponses.find(resp => resp?.data?.Data?.lead_id)?.data?.Data?.lead_id;
                if (leadId) {
                    setMessage({ 
                        type: 'success', 
                        msg: `🎉 Congratulations! Your application has been submitted successfully! Your Application ID: ${leadId}. Our teammate will contact you soon for further processing.`, 
                        place: "global" 
                    });
                    setStorage("lead_id", leadId);
                    setStorage("application_status", "submitted");
                } else {
                    setMessage({ 
                        type: 'success', 
                        msg: '🎉 Congratulations! Your application has been submitted successfully! Our teammate will contact you soon for further processing.', 
                        place: "global" 
                    });
                    setStorage("application_status", "submitted");
                }
                
                setStorage("next_step", successResponses[0]?.data?.Data?.next_step);
                setStorage('percent', successResponses[0]?.data?.Data?.step_percentage);
                
                // Navigate to next step as per original flow
                handleEvent(getStorage('next_step'));
            } else {
                const failed = responses.find((resp) => resp?.data?.Status !== 1);
                setMessage({
                  type: "error",
                  msg: failed?.data?.Message || "Some documents failed to upload. Please try again.",
                });
            }
        } catch (error) {
            setMessage({ type: "error", msg: "An error occurred while uploading documents. Please try again." });
        }

        setLoading(false);
    };

    return (
        <>
       
        <BoxWrapper className="w100">
            {/* Application Submitted - Frozen State */}
            {isApplicationSubmitted && (
                <div style={{
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #28a745',
                    borderRadius: '10px',
                    padding: '30px',
                    margin: '20px',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: '60px', color: '#28a745', marginBottom: '20px' }}>
                        ✅
                    </div>
                    <h2 style={{ color: '#28a745', marginBottom: '15px' }}>
                        Application Already Submitted!
                    </h2>
                    <p style={{ color: '#666', fontSize: '16px', margin: '15px 0' }}>
                        Your application is currently under review. Our teammate will contact you soon.
                    </p>
                    <p style={{ color: '#888', fontSize: '14px', margin: '10px 0' }}>
                        You cannot upload documents until your current application status is closed.
                    </p>
                </div>
            )}

            {!isApplicationSubmitted && (
            <div className="formmainBox flex">
                <div className="left">
                    {/* <div className='center gap4 pointer' onClick={() => goBack(navigate, "/my-dashboard/about-your-company")}>
                        <img src={arrowIcon} alt="" /> <span>Back</span>
                    </div> */}
                </div>
                <div className="right">
                    <h2>Upload Your Documents</h2>
                    <p>Upload your documents to verify your details</p>

                    <FormWrapper2>
                        <Alert setMessage={setMessage} message={message} />

                        {isLoadingDocs ? (  // Show loading spinner when documents are being fetched
                            <p>Loading required documents...</p>
                        ) : (
                            documentsRequired.length > 0 ? (
                                documentsRequired.map(doc => (
                                    <div key={doc.name} className="inputBox">
                                        <h2 className="subheading small">{doc.name} <span style={{color:"red"}}>*</span> </h2>


                                        {/* Use PictureUpload component for consistent design */}
                                        <PictureUpload
                                            setImage={(file) => handleFileChange(doc.name, file)}
                                            image={uploadedDocuments[doc.name] || ""}
                                            accept={doc.allowed_format === "image" ? "image/*" : "application/pdf"}
                                            type={doc.allowed_format === "image" ? "image" : "file"}
                                        />

                                        {/* Password field for PDF documents */}
                                        {(doc.allowed_format === "pdf" || doc.name.toLowerCase().includes("bank") || doc.name.toLowerCase().includes("salary")) && (
                                            <div style={{ marginTop: "15px" }}>
                                                <label style={{ 
                                                    display: "block", 
                                                    marginBottom: "8px", 
                                                    fontSize: "14px", 
                                                    fontWeight: "500",
                                                    color: "#333"
                                                }}>
                                                    Password (Optional)
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter password if PDF is password protected"
                                                    value={documentPasswords[doc.name] || ""}
                                                    onChange={(e) => handlePasswordChange(doc.name, e.target.value)}
                                                    style={{
                                                        width: "100%",
                                                        padding: "12px",
                                                        border: "1px solid #ddd",
                                                        borderRadius: "6px",
                                                        fontSize: "14px",
                                                        outline: "none",
                                                        transition: "border-color 0.3s ease"
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = "#007bff"}
                                                    onBlur={(e) => e.target.style.borderColor = "#ddd"}
                                                />
                                                <small style={{ 
                                                    color: "#6c757d", 
                                                    fontSize: "12px", 
                                                    marginTop: "4px", 
                                                    display: "block"
                                                }}>
                                                    Leave empty if not password protected
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p>No documents are required at this moment.</p>
                            )
                        )}

                        {/* Conditionally render the button only if documents are loaded */}
                        {!isLoadingDocs && documentsRequired.length > 0 && (
                            <div className="button">
                                <Button 
                                    title="Continue" 
                                    onClick={submitDocuments} 
                                    loading={loading} 
                                    disabled={loading || isLoadingDocs}  // Disable button while uploading or fetching
                                />
                            </div>
                        )}
                    </FormWrapper2>
                </div>
            </div>
            )}
        </BoxWrapper>
        </>
    );
}

export default DocumentUpload;
